import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  DayCalendar,
  DayCalendarColumns,
  SegmentedControl,
} from '@bookaburra/ui';
import { AlertTriangle, Check, ChevronLeft, ChevronRight, Lock, LogIn, Plus } from 'lucide-react';
import { SUPPORTED_LOCALES } from '@bookaburra/i18n';
import { AdminApiError, fetchAdminCalendar, type AdminOverride } from '../booking-flow/admin-api';
import { fetchBookingTenant } from '../booking-flow/api';
import { fetchCapabilities } from '../booking-flow/capabilities-api';
import { fetchClosedDays } from '../booking-flow/closed-days-api';
import { fetchMembers } from '../booking-flow/members-api';
import {
  fetchAdminServices,
  type AdminServiceWithTranslations,
} from '../booking-flow/translations-api';
import { fetchMyMemberships } from '../booking-flow/tenant-api';
import { useDocumentTitle } from '../lib/use-document-title';
import { BookingMoveDialog } from '../components/BookingMoveDialog';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const Route = createFileRoute('/admin/$slug/')({
  component: AdminCalendar,
});

/** Number of days rendered side-by-side. Today + next two. */
const DAY_WINDOW = 3;

type CalendarView = 'merged' | 'by-staff' | 'by-resource';

function AdminCalendar() {
  const { slug } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();

  // Regular staff don't get the bookings calendar — their landing
  // surface is /my-day. Redirect on the client so a staff member who
  // bookmarks /admin/$slug doesn't end up on a screen they can't act on.
  const myMembershipsQ = useQuery({
    queryKey: ['me-memberships'],
    queryFn: fetchMyMemberships,
    staleTime: 60_000,
  });
  const myRole = myMembershipsQ.data?.memberships.find((m) => m.tenant.slug === slug)?.role;
  useEffect(() => {
    if (myRole === 'staff') {
      void navigate({ to: '/admin/$slug/my-day', params: { slug }, replace: true });
    }
  }, [myRole, slug, navigate]);
  const [day, setDay] = useState<Date>(() => startOfDay(new Date()));
  const [view, setView] = useState<CalendarView>('merged');
  const [editingBookingId, setEditingBookingId] = useState<string | null>(null);
  // All views span 3 days. Merged renders 3 columns side-by-side (one
  // day per column); column views stack the 3 days vertically — each
  // day gets its own multi-column block so staff/resource columns
  // stay wide enough to read.
  const days = DAY_WINDOW;
  const from = day.getTime();
  const to = from + days * MS_PER_DAY;

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-calendar', slug, from, to],
    queryFn: () => fetchAdminCalendar(slug, from, to),
  });

  // If the operator was on a column view and the corresponding list
  // disappeared (e.g. the only resource was archived), snap back to
  // merged so they don't get stuck looking at an empty "By resource"
  // screen.
  useEffect(() => {
    if (!data) return;
    if (view === 'by-staff' && data.staff.length === 0) setView('merged');
    if (view === 'by-resource' && (data.resources?.length ?? 0) === 0)
      setView('merged');
  }, [data, view]);

  // Once we know the tenant timezone, realign the default day so
  // "today" matches the tenant's wall clock — not the browser's. The
  // initial render uses browser-local midnight as a placeholder so
  // SSR / first paint isn't blocked, then this effect corrects it.
  const tenantTz = data?.timezone;
  const dayInitAligned = useState(false);
  const [aligned, setAligned] = dayInitAligned;
  useEffect(() => {
    if (!tenantTz || aligned) return;
    const today = startOfDayInTz(new Date(), tenantTz);
    if (today.getTime() !== day.getTime()) setDay(today);
    setAligned(true);
    // We only realign on the first arrival of the tenant tz. After
    // that, Prev/Today/Next mutate `day` directly and we don't fight
    // the operator.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantTz]);

  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  useDocumentTitle(
    tenantQ.data ? `${tenantQ.data.tenant.name} Admin` : null,
  );

  const timezone = data?.timezone ?? 'Australia/Sydney';
  const tenantDefaultLocale = tenantQ.data?.tenant.defaultLocale ?? 'en-AU';

  /** Resolve the short language label to show on the pill — only
   *  when the booking was made in a non-default language. Falls
   *  back to the raw code if it's not in our SUPPORTED_LOCALES map
   *  (e.g. a browser sent Accept-Language pt-BR). */
  function localeLabelFor(code: string | null): string | null {
    if (!code) return null;
    if (code === tenantDefaultLocale) return null;
    const match = SUPPORTED_LOCALES.find((l) => l.code === code);
    return match ? match.english : code;
  }

  return (
    <main className="mx-auto max-w-7xl px-6 py-10 sm:px-12">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Bookings calendar</h1>
          <p className="text-sm text-muted-foreground">
            {timezone}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ViewToggle
            view={view}
            onChange={setView}
            staffCount={data?.staff.length ?? 0}
            resourceCount={data?.resources?.length ?? 0}
          />
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDay(addDays(day, -days))}
              leadingIcon={<ChevronLeft className="h-4 w-4" />}
            >
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setDay(
                  tenantTz
                    ? startOfDayInTz(new Date(), tenantTz)
                    : startOfDay(new Date()),
                )
              }
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDay(addDays(day, days))}
              trailingIcon={<ChevronRight className="h-4 w-4" />}
            >
              Next
            </Button>
          </div>
        </div>
      </header>

      <CreateBookingCta slug={slug} />

      <SetupChecklist slug={slug} staff={data?.staff ?? []} />

      {isLoading ? <CalendarSkeleton /> : null}
      {error instanceof AdminApiError && error.status === 401 ? (
        <SignInPrompt slug={slug} />
      ) : error instanceof AdminApiError && error.status === 403 ? (
        <NoAccessPrompt />
      ) : error instanceof AdminApiError && error.status === 404 ? (
        <Alert variant="destructive">
          <AlertTitle>Tenant not found</AlertTitle>
          <AlertDescription>
            <code>{slug}</code> isn&rsquo;t a registered tenant.
          </AlertDescription>
        </Alert>
      ) : error ? (
        <Alert variant="destructive">
          <AlertTitle>Couldn&rsquo;t load calendar</AlertTitle>
          <AlertDescription>{(error as Error).message}</AlertDescription>
        </Alert>
      ) : null}

      {data && view === 'merged' ? (
        <div className="grid gap-5 xl:grid-cols-3">
          {Array.from({ length: DAY_WINDOW }, (_, i) => addDays(day, i)).map((d) => (
            <div key={d.toISOString()} className="space-y-3">
              <DayStaffStrip
                day={d}
                timezone={timezone}
                staff={data.staff}
                overrides={data.overrides}
              />
              <DayCalendar
                day={d}
                timezone={timezone}
                bookings={data.bookings
                  .filter((b) => sameDay(new Date(b.rangeStart), d, timezone))
                  .map((b) => ({
                    id: b.id,
                    serviceName: b.serviceName,
                    rangeStart: b.rangeStart,
                    clientName: b.clientName,
                    clientLastOutcome: b.clientLastOutcome ?? null,
                    staffIds: b.staffIds,
                    localeLabel: localeLabelFor(b.bookingLocale),
                    isSynthetic: b.isSynthetic ?? false,
                  }))}
                staff={data.staff}
                onBookingClick={setEditingBookingId}
              />
            </div>
          ))}
        </div>
      ) : null}

      {data && view !== 'merged' ? (
        <div className="space-y-6">
          {Array.from({ length: DAY_WINDOW }, (_, i) => addDays(day, i)).map((d) => (
            <div key={d.toISOString()} className="space-y-3">
              <DayStaffStrip
                day={d}
                timezone={timezone}
                staff={data.staff}
                overrides={data.overrides}
              />
              <DayCalendarColumns
                day={d}
                timezone={timezone}
                groupBy={view === 'by-staff' ? 'staff' : 'resource'}
                columns={
                  view === 'by-staff'
                    ? data.staff.map((s) => ({ id: s.id, name: s.name }))
                    : (data.resources ?? []).map((r) => ({ id: r.id, name: r.name }))
                }
                bookings={data.bookings
                  .filter((b) => sameDay(new Date(b.rangeStart), d, timezone))
                  .map((b) => ({
                    id: b.id,
                    serviceName: b.serviceName,
                    rangeStart: b.rangeStart,
                    clientName: b.clientName,
                    clientLastOutcome: b.clientLastOutcome ?? null,
                    staffIds: b.staffIds,
                    resourceIds: b.resourceIds,
                    localeLabel: localeLabelFor(b.bookingLocale),
                    isSynthetic: b.isSynthetic ?? false,
                  }))}
                staff={data.staff}
                onBookingClick={setEditingBookingId}
              />
            </div>
          ))}
        </div>
      ) : null}

      {editingBookingId ? (
        <BookingMoveDialog
          slug={slug}
          bookingId={editingBookingId}
          timezone={timezone}
          booking={
            data?.bookings.find((b) => b.id === editingBookingId) ?? null
          }
          onClose={() => setEditingBookingId(null)}
          onMoved={() => {
            qc.invalidateQueries({ queryKey: ['admin-calendar', slug] });
            setEditingBookingId(null);
          }}
          followUpHref={(clientId) =>
            `/admin/${encodeURIComponent(slug)}/create-booking?clientId=${encodeURIComponent(clientId)}`
          }
        />
      ) : null}
    </main>
  );
}

function ViewToggle({
  view,
  onChange,
  staffCount,
  resourceCount,
}: {
  view: CalendarView;
  onChange: (v: CalendarView) => void;
  staffCount: number;
  resourceCount: number;
}) {
  // Hide a view option when its grouping has nothing to show — saves
  // the operator a click into an empty state. Merged is always
  // available because there's always at least the booking list.
  const opts: { value: CalendarView; label: string; hidden?: boolean }[] = [
    { value: 'merged', label: 'Merged' },
    { value: 'by-staff', label: 'By staff', hidden: staffCount === 0 },
    { value: 'by-resource', label: 'By resource', hidden: resourceCount === 0 },
  ];
  const visible = opts.filter((o) => !o.hidden);
  // If everything except Merged is hidden the toggle has no purpose —
  // drop it entirely rather than show a single-option control.
  if (visible.length <= 1) return null;
  return (
    <SegmentedControl<CalendarView>
      value={view}
      onChange={onChange}
      ariaLabel="Calendar view"
      options={visible.map((o) => ({ value: o.value, label: o.label }))}
    />
  );
}

/**
 * One-row staff availability summary that sits above each day's
 * calendar. Each staff member is a badge; if they have an override
 * overlapping this day in the tenant zone, the badge colour + label
 * reflects it (sick / off / extra). Removes the need for a separate
 * "Staff overrides today" alert + bottom-of-page roster strip.
 */
function DayStaffStrip({
  day,
  timezone,
  staff,
  overrides,
}: {
  day: Date;
  timezone: string;
  staff: { id: string; name: string; role: string }[];
  overrides: AdminOverride[];
}) {
  const dayRange = localDayRange(day, timezone);
  // Index overrides by staffId, keeping the most-blocking type if
  // multiple overlap the same day for one person.
  const byStaff = new Map<string, AdminOverride['type']>();
  for (const o of overrides) {
    if (!rangesOverlap(o.rangeStart, o.rangeEnd, dayRange.start, dayRange.end)) {
      continue;
    }
    const current = byStaff.get(o.staffId);
    // Priority: sick > time_off > extra (so a sick+extra pair
    // surfaces the sick badge).
    if (current === 'sick') continue;
    if (current === 'time_off' && o.type !== 'sick') continue;
    byStaff.set(o.staffId, o.type);
  }

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-lg border border-brand-sage/15 bg-card/60 px-3 py-2">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        Team ({staff.length})
      </span>
      {staff.length === 0 ? (
        <span className="text-xs text-muted-foreground">
          No staff — invite a teammate
        </span>
      ) : null}
      {staff.map((s) => {
        const ovr = byStaff.get(s.id);
        const label = ovr === 'sick'
          ? `${s.name} · sick`
          : ovr === 'time_off'
            ? `${s.name} · off`
            : ovr === 'extra'
              ? `${s.name} · extra`
              : s.name;
        const variant: 'destructive' | 'warning' | 'success' | 'secondary' =
          ovr === 'sick'
            ? 'destructive'
            : ovr === 'time_off'
              ? 'warning'
              : ovr === 'extra'
                ? 'success'
                : 'secondary';
        return (
          <Badge key={s.id} variant={variant}>
            {label}
          </Badge>
        );
      })}
    </div>
  );
}

/** UTC start + end-of-day for `day` rendered in the supplied IANA tz. */
function localDayRange(day: Date, timezone: string): { start: number; end: number } {
  // Get the YYYY-MM-DD wall-clock date in `timezone` for the
  // instant `day` represents. Then build that local midnight, and
  // the next day's local midnight, as epoch ms.
  const ymd = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(day);
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) {
    const t = day.getTime();
    return { start: t, end: t + 86_400_000 };
  }
  const start = utcAtLocalMidnight(y, m, d, timezone);
  const end = utcAtLocalMidnight(y, m, d + 1, timezone);
  return { start, end };
}

function utcAtLocalMidnight(y: number, m: number, d: number, timezone: string): number {
  // Approximate UTC for the local midnight, then correct using the
  // tz offset at that approximate instant. Same trick as
  // apps/web/src/api/slots.ts.
  const approxUtc = Date.UTC(y, m - 1, d, 0, 0, 0);
  const offsetMs = tzOffsetMs(approxUtc, timezone);
  return approxUtc - offsetMs;
}

function tzOffsetMs(instantMs: number, timezone: string): number {
  const dtf = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(new Date(instantMs));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const localAsUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return localAsUtc - instantMs;
}

function rangesOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return aStart < bEnd && bStart < aEnd;
}

function CalendarSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-brand-sage/20 bg-brand-cream shadow-brand">
      <div className="h-12 border-b border-brand-sage/15 bg-brand-cream-shadow" />
      <div className="space-y-2 p-5">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-8 animate-pulse rounded-md bg-brand-cream-shadow" />
        ))}
      </div>
    </div>
  );
}

function SignInPrompt({ slug }: { slug: string }) {
  const redirect = encodeURIComponent(`/admin/${slug}`);
  return (
    <Card className="mx-auto max-w-md p-8 text-center">
      <Lock className="mx-auto h-8 w-8 text-muted-foreground" />
      <h2 className="mt-4 text-lg font-semibold">Sign in to continue</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        The staff calendar is only available to signed-in team members.
      </p>
      <div className="mt-6">
        <Link to="/signin" search={{ redirect: `/admin/${slug}` }}>
          <Button leadingIcon={<LogIn className="h-4 w-4" />}>Sign in</Button>
        </Link>
      </div>
      {/* `redirect` is included as a hidden link target so e2e tests can
          discover the destination without re-parsing the URL */}
      <span className="hidden" data-redirect={redirect} />
    </Card>
  );
}

function NoAccessPrompt() {
  return (
    <Alert variant="destructive">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>You don&rsquo;t have access to this tenant</AlertTitle>
      <AlertDescription>
        Your account isn&rsquo;t a member of this business. Ask the owner to
        invite you, or sign in with a different account.
      </AlertDescription>
    </Alert>
  );
}

/**
 * "+ Create booking" entry point — Feature 6.
 *
 * Hidden from regular staff entirely (they don't have access to the
 * create-booking flow). For owner + admin: a brand-sage filled button
 * + a tip line nudging them to click an existing booking when they
 * want a follow-up for the same client.
 */
function CreateBookingCta({ slug }: { slug: string }) {
  const meQ = useQuery({
    queryKey: ['me-memberships'],
    queryFn: fetchMyMemberships,
    staleTime: 60_000,
  });
  const role = meQ.data?.memberships.find((m) => m.tenant.slug === slug)?.role;
  if (role !== 'owner' && role !== 'manager') return null;
  return (
    <div className="mb-6 flex flex-wrap items-center gap-3">
      <Link to="/admin/$slug/create-booking" params={{ slug }}>
        <Button leadingIcon={<Plus className="h-4 w-4" />}>Create booking</Button>
      </Link>
      <p className="text-xs text-muted-foreground">
        Tip: click any existing booking to create a follow-up for that client.
      </p>
    </div>
  );
}

function startOfDay(d: Date): Date {
  // Browser-local midnight. Fine as a temporary default before we
  // know the tenant timezone; `startOfDayInTz` is the authoritative
  // version once the calendar API has responded.
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

/**
 * UTC instant corresponding to midnight on `d`'s wall-clock date as
 * observed in `timezone`. Solves the "it's 11:51pm Thu local but
 * Adelaide is still Wed" bug: we want the day that matches the
 * tenant's wall clock, not the browser's.
 *
 * Algorithm: format `d` in the tenant zone to get YYYY-MM-DD, then
 * iterate hour-by-hour from the day-before-UTC's noon to find the
 * smallest UTC instant whose tenant-local date equals the target.
 * Cheap enough (≤48 iterations) and avoids pulling in a date lib.
 */
function startOfDayInTz(d: Date, timezone: string): Date {
  const ymd = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  // Start ~36h before the target — covers any plausible UTC offset
  // (-12 to +14) so the search definitely brackets the local midnight.
  let ms = d.getTime() - 36 * 60 * 60 * 1000;
  while (fmt.format(new Date(ms)) !== ymd) {
    ms += 60 * 60 * 1000;
    // Safety bound — never iterate past ~3 days.
    if (ms > d.getTime() + 36 * 60 * 60 * 1000) break;
  }
  return new Date(ms);
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getTime() + n * MS_PER_DAY);
}

function sameDay(a: Date, b: Date, timezone: string): boolean {
  const fmt = new Intl.DateTimeFormat('en-AU', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(a) === fmt.format(b);
}

/** Local hour (0-23) of an epoch ms in the tenant's zone. */
function hourInZone(epochMs: number, timezone: string): number {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: timezone,
    hour: 'numeric',
    hour12: false,
  }).formatToParts(new Date(epochMs));
  const h = parts.find((p) => p.type === 'hour')?.value ?? '0';
  return Number(h) || 0;
}

function formatHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

/**
 * Setup-checklist nudge shown above the calendar until the tenant has
 * the four things bookings actually need: business hours, address,
 * a service, and a teammate beyond the owner. Once every item is
 * ticked off the card disappears.
 *
 * Each row reads from a data source the calendar page already has on
 * hand (tenant payload for hours/address, calendar staff list for
 * team) or fetches lazily (services list). The card never blocks the
 * page, just nudges.
 */
function SetupChecklist({
  slug,
  staff,
}: {
  slug: string;
  staff: { id: string; name: string; role: string }[];
}) {
  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  const servicesQ = useQuery({
    queryKey: ['admin-services-count', slug],
    queryFn: () => fetchAdminServices(slug),
    staleTime: 60_000,
  });
  const closedDaysQ = useQuery({
    queryKey: ['admin-closed-days', slug],
    queryFn: () => fetchClosedDays(slug),
    staleTime: 60_000,
  });
  const membersQ = useQuery({
    queryKey: ['admin-members', slug],
    queryFn: () => fetchMembers(slug),
    staleTime: 60_000,
  });
  const capabilitiesQ = useQuery({
    queryKey: ['admin-capabilities', slug],
    queryFn: () => fetchCapabilities(slug),
    staleTime: 60_000,
  });

  if (!tenantQ.data) return null;

  const tenant = tenantQ.data.tenant;
  const hasHours = Boolean(tenant.businessHours);
  const hasAddress = Boolean(tenant.address);
  const hasService = (servicesQ.data?.services?.length ?? 0) > 0;
  // Owner is created with the tenant; consider the team "set up" once
  // there's at least one extra member.
  const hasTeammate = staff.length > 1;
  // "Have they used the feature at all" check — any closed day with
  // a date >= today (in the tenant's timezone) counts. Past entries
  // don't unblock the nudge; the operator needs to think about the
  // *next* public holiday, not the last one.
  const todayInTenantTz = new Intl.DateTimeFormat('en-CA', {
    timeZone: tenant.timezone,
  }).format(new Date());
  const hasFutureClosedDay = (closedDaysQ.data?.closedDays ?? []).some(
    (d) => d.date >= todayInTenantTz,
  );

  const allServices = servicesQ.data?.services ?? [];

  // Postcode whitelist for on-site services. Only nag if the tenant
  // actually offers on-site bookings — for desk-only / virtual-only
  // businesses the whitelist doesn't apply.
  const hasOnSiteService = allServices.some(
    (s) => s.archivedAt == null && s.locationType === 'on_site',
  );
  const postcodeWhitelistDone =
    !hasOnSiteService || (tenant.serviceablePostcodes?.length ?? 0) > 0;

  // Skill + feature coverage. A service whose requirements can't be
  // met by the current team / resources is unbookable — the allocator
  // never finds a match. The row's `done` flag aggregates across
  // every active service.
  const allocatorCoverage = summariseAllocatorCoverage(
    allServices,
    membersQ.data?.members ?? null,
    capabilitiesQ.data ?? null,
  );

  // Translations only nag the operator if they've shown intent to use
  // them — either they've already written a translation, or they
  // run on a non-English default locale. English-only tenants never
  // see the row.
  const hasAnyTranslation = allServices.some((s) => s.translations.length > 0);
  const i18nIntent =
    hasAnyTranslation ||
    Boolean(tenant.defaultLocale && tenant.defaultLocale !== 'en-AU');
  const translationsSummary = i18nIntent
    ? summariseTranslations(allServices)
    : null;
  const translationsDone =
    !translationsSummary || translationsSummary.done;

  if (
    hasHours &&
    hasAddress &&
    hasService &&
    hasTeammate &&
    hasFutureClosedDay &&
    postcodeWhitelistDone &&
    allocatorCoverage.done &&
    translationsDone
  )
    return null;

  return (
    <Card className="mb-6 border-primary/20 bg-primary/5 p-5">
      <p className="text-xs font-semibold uppercase tracking-widest text-primary">
        Finish setting up
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        A few things to do before the booking page is ready.
      </p>
      <ul className="mt-4 space-y-2 text-sm">
        <ChecklistRow
          done={hasHours}
          label="Set business hours"
          help="The booking calendar uses these to bound bookable times."
          to="/admin/$slug/settings"
          slug={slug}
        />
        <ChecklistRow
          done={hasAddress}
          label="Add your business address"
          help="Shown to clients on confirmations and the booking page."
          to="/admin/$slug/settings"
          slug={slug}
        />
        <ChecklistRow
          done={hasService}
          label="Add at least one service"
          help="Without a service there's nothing for clients to book."
          to="/admin/$slug/services"
          slug={slug}
        />
        <ChecklistRow
          done={hasTeammate}
          label="Invite a teammate"
          help="Optional, but you'll outgrow taking every booking yourself."
          to="/admin/$slug/members"
          slug={slug}
        />
        <ChecklistRow
          done={hasFutureClosedDay}
          label="Block your public holidays"
          help="Add the next few holidays / planned closures so clients can't book on them."
          to="/admin/$slug/closed-days"
          slug={slug}
        />
        {hasOnSiteService ? (
          <ChecklistRow
            done={postcodeWhitelistDone}
            label="Set your service area"
            help={
              postcodeWhitelistDone
                ? 'Postcodes you cover for on-site jobs.'
                : 'On-site bookings are accepted from anywhere until you list the postcodes you actually cover.'
            }
            to="/admin/$slug/settings"
            slug={slug}
          />
        ) : null}
        <ChecklistRow
          done={allocatorCoverage.done}
          label="Every service requirement is fillable"
          help={allocatorCoverage.helpText}
          to="/admin/$slug/services"
          slug={slug}
        />
        {translationsSummary ? (
          <ChecklistRow
            done={translationsSummary.done}
            label="Translate every service"
            help={translationsSummary.helpText}
            to="/admin/$slug/translations"
            slug={slug}
          />
        ) : null}
      </ul>
    </Card>
  );
}

/**
 * Decide whether each active service's allocator requirements (the
 * skills its staff slots demand, and the resource features it
 * requires) can be fulfilled by the tenant's current team and
 * resources. If a service requires a skill no member has, the
 * allocator can't book it — same with features and resources.
 *
 * The feature check is conditional: a tenant with zero resources
 * defined isn't using the resources system at all, so a service's
 * `requiredFeatureIds` is moot. We only nag once at least one
 * resource exists.
 *
 * Returns `done: true` when every active service is fillable. When
 * not, the help text lists up to three affected services with a
 * count of unfilled requirements (skills + features combined).
 */
function summariseAllocatorCoverage(
  services: AdminServiceWithTranslations[],
  members: { skills: { skillId: string }[] }[] | null,
  capabilities: {
    skills: { id: string; archivedAt?: number | null }[];
    features: { id: string; archivedAt?: number | null }[];
    resources: { featureIds: string[]; archivedAt?: number | null }[];
  } | null,
): { done: boolean; helpText: string } {
  // Data isn't loaded yet — render the row in its "done" state until
  // we know better, so the card doesn't flash a false negative on
  // first paint.
  if (!members || !capabilities) {
    return {
      done: true,
      helpText: 'All service requirements covered by your team and resources.',
    };
  }
  const active = services.filter((s) => s.archivedAt == null);
  if (active.length === 0) {
    return {
      done: true,
      helpText: 'All service requirements covered by your team and resources.',
    };
  }

  const memberSkillIds = new Set<string>();
  for (const m of members) for (const s of m.skills) memberSkillIds.add(s.skillId);

  const activeResources = capabilities.resources.filter((r) => r.archivedAt == null);
  const resourceFeatureIds = new Set<string>();
  for (const r of activeResources) for (const f of r.featureIds) resourceFeatureIds.add(f);
  const checkFeatures = activeResources.length > 0;

  const problems: { name: string; count: number }[] = [];
  for (const svc of active) {
    let missing = 0;
    for (const slot of svc.staffSlots) {
      for (const skillId of slot.skills) {
        if (!memberSkillIds.has(skillId)) missing += 1;
      }
    }
    if (checkFeatures) {
      for (const featureId of svc.requiredFeatureIds) {
        if (!resourceFeatureIds.has(featureId)) missing += 1;
      }
    }
    if (missing > 0) problems.push({ name: svc.name, count: missing });
  }
  if (problems.length === 0) {
    return {
      done: true,
      helpText: 'All service requirements covered by your team and resources.',
    };
  }
  problems.sort((a, b) => b.count - a.count);
  const SHOW = 3;
  const shown = problems.slice(0, SHOW).map((p) => `${p.name} (${p.count})`);
  const more = problems.length - shown.length;
  const tail = more > 0 ? `, and ${more} more` : '';
  return {
    done: false,
    helpText: `${shown.join(', ')}${tail} — requirements no one on your team or resources can fulfil.`,
  };
}

/**
 * Summarise translation coverage across all active services. Drives
 * the optional "Translate every service" row in the SetupChecklist —
 * shown only for tenants who've signalled i18n intent (a non-English
 * default locale, or at least one translation row already written).
 *
 * "Needs attention" counts both missing locales AND stale rows (the
 * English source has been edited since the translation was saved).
 * Listed services are sorted by attention-needed desc and truncated
 * so the line fits the card. Names aren't enumerated past the first
 * three — the operator gets the gist and opens the translations
 * page for the full list.
 */
function summariseTranslations(
  services: AdminServiceWithTranslations[],
): { done: boolean; helpText: string } {
  const translatableCodes = SUPPORTED_LOCALES
    .filter((l) => l.code !== 'en-AU')
    .map((l) => l.code);
  const active = services.filter((s) => s.archivedAt == null);
  if (active.length === 0) {
    return {
      done: false,
      helpText: 'Add a service first, then translate it.',
    };
  }
  const problems: { name: string; count: number }[] = [];
  for (const svc of active) {
    const nameRows = svc.translations.filter((t) => t.field === 'name');
    const covered = new Set(nameRows.map((t) => t.locale));
    let missing = 0;
    for (const code of translatableCodes) if (!covered.has(code)) missing += 1;
    const stale = nameRows.filter(
      (t) => t.sourceValue != null && t.sourceValue !== svc.name,
    ).length;
    const total = missing + stale;
    if (total > 0) problems.push({ name: svc.name, count: total });
  }
  if (problems.length === 0) {
    return {
      done: true,
      helpText: 'Every service translated for every supported language.',
    };
  }
  problems.sort((a, b) => b.count - a.count);
  const SHOW = 3;
  const shown = problems.slice(0, SHOW).map((p) => `${p.name} (${p.count})`);
  const more = problems.length - shown.length;
  const tail = more > 0 ? `, and ${more} more` : '';
  return {
    done: false,
    helpText: `${shown.join(', ')}${tail}`,
  };
}

function ChecklistRow({
  done,
  label,
  help,
  to,
  slug,
}: {
  done: boolean;
  label: string;
  help: string;
  to:
    | '/admin/$slug/settings'
    | '/admin/$slug/services'
    | '/admin/$slug/members'
    | '/admin/$slug/closed-days'
    | '/admin/$slug/translations';
  slug: string;
}) {
  return (
    <li className="flex items-start gap-3">
      <span
        aria-hidden
        className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
          done
            ? 'bg-primary text-primary-foreground'
            : 'border border-primary/40 text-primary'
        }`}
      >
        {done ? <Check className="h-3 w-3" /> : null}
      </span>
      <div className="min-w-0 flex-1">
        <p className={done ? 'font-medium text-muted-foreground line-through' : 'font-medium'}>
          {label}
        </p>
        <p className="text-xs text-muted-foreground">{help}</p>
      </div>
      {!done ? (
        <Link
          to={to}
          params={{ slug }}
          className="shrink-0 text-sm font-medium text-primary underline-offset-2 hover:underline"
        >
          Open →
        </Link>
      ) : null}
    </li>
  );
}
